import React from 'react'
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu'
import Button from '@atlaskit/button'
import MoreIcon from '@atlaskit/icon/glyph/more'
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle'
import UndoIcon from '@atlaskit/icon/glyph/undo'
import TrashIcon from '@atlaskit/icon/glyph/trash'
import RefreshIcon from '@atlaskit/icon/glyph/refresh'
import EditorEditIcon from '@atlaskit/icon/glyph/editor/edit'
import { token } from '@atlaskit/tokens'

/**
 * RUN ACTIONS DROPDOWN
 *
 * Dropdown menu for test run actions shown in run header.
 * Displays a three-dot menu that expands to show available actions.
 *
 * Actions include:
 * - Edit Run: Opens existing EditRunModal for field editing
 * - Mark as Completed / Mark as Active: Toggle completion status
 * - Clear Run: Resets all execution statuses (shows confirmation)
 * - Delete Run: Permanently removes run (shows confirmation)
 *
 * Uses shouldRenderToParent to prevent portal issues in table cells.
 *
 * @param {object} run - The test run object containing id, isCompleted, etc.
 * @param {number} runNumber - The run number (1-indexed) for display
 * @param {function} onEdit - Callback to open edit modal
 * @param {function} onToggleCompleted - Callback to toggle completed status, receives (isCompleted)
 * @param {function} onClear - Callback to open clear confirmation
 * @param {function} onDelete - Callback to open delete confirmation
 */
export function RunActionsDropdown({
  run,
  runNumber,
  onEdit,
  onToggleCompleted,
  onClear,
  onDelete
}) {
  return (
    <DropdownMenu
      trigger={({ triggerRef, ...props }) => (
        <Button
          {...props}
          ref={triggerRef}
          appearance="subtle"
          spacing="compact"
          iconBefore={
            <MoreIcon
              label="Run actions"
              size="small"
              primaryColor={token('color.icon.subtle', '#6B778C')}
            />
          }
        />
      )}
      shouldRenderToParent  // Critical: prevents portal issues in table cells
      placement="bottom-end"
    >
      {/* Primary actions group */}
      <DropdownItemGroup>
        {/* Edit Run - opens field editing modal */}
        <DropdownItem
          onClick={onEdit}
          elemBefore={<EditorEditIcon label="Edit" size="small" />}
        >
          Edit Run
        </DropdownItem>

        {/* Toggle Completed/Active status - shows appropriate option based on current state */}
        {run.isCompleted ? (
          <DropdownItem
            onClick={() => onToggleCompleted(false)}
            elemBefore={<UndoIcon label="Mark as Active" size="small" />}
          >
            Mark as Active
          </DropdownItem>
        ) : (
          <DropdownItem
            onClick={() => onToggleCompleted(true)}
            elemBefore={
              <CheckCircleIcon
                label="Mark as Completed"
                size="small"
                primaryColor={token('color.icon.success', '#22A06B')}
              />
            }
          >
            Mark as Completed
          </DropdownItem>
        )}
      </DropdownItemGroup>

      {/* Destructive actions group - separated with a divider */}
      <DropdownItemGroup hasSeparator>
        {/* Clear Run - resets all execution statuses */}
        <DropdownItem
          onClick={onClear}
          elemBefore={
            <RefreshIcon
              label="Clear Run"
              size="small"
              primaryColor={token('color.icon.warning', '#FF8B00')}
            />
          }
        >
          Clear Run
        </DropdownItem>

        {/* Delete Run - permanently removes run and all data */}
        <DropdownItem
          onClick={onDelete}
          elemBefore={
            <TrashIcon
              label="Delete Run"
              size="small"
              primaryColor={token('color.icon.danger', '#DE350B')}
            />
          }
        >
          Delete Run
        </DropdownItem>
      </DropdownItemGroup>
    </DropdownMenu>
  )
}
